import { MapPinned, SearchX, ShieldAlert } from 'lucide-react';
import { Table } from 'antd';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { ListSearch } from '../../../../../shared/components/ListSearch';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import type { useFlaggedAreaList } from '../hooks/use-flagged-area-list';
import type { FlaggedArea } from '../flagged-areas.api';
import { buildFlaggedAreaColumns } from './build-flagged-area-columns';

interface FlaggedAreasTableProps {
  areas: ReturnType<typeof useFlaggedAreaList>;
  onSetActive: (area: FlaggedArea, isActive: boolean) => void;
}

/** Skeleton on first load, then the server-paged table; the old rows stay up, dimmed, while the next page loads. */
export function FlaggedAreasTable({ areas, onSetActive }: FlaggedAreasTableProps) {
  const { state, list, isRefreshing } = areas;
  if (state.status === 'error') {
    return (
      <EmptyState
        icon={ShieldAlert}
        title="Couldn't load flagged areas"
        description="Something went wrong loading the flagged areas. Try reloading the page."
      />
    );
  }
  if (state.status !== 'loaded') return <TableSkeleton label="Loading flagged areas" withSearch />;
  if (state.page.total === 0 && !list.isFiltered) {
    return (
      <EmptyState
        icon={MapPinned}
        title="No flagged areas yet"
        description="Mark the places where a stop is dangerous, and the rest places where an overnight stop is fine, so the unsafe-stop alert knows the difference."
      />
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <ListSearch value={list.query.q} placeholder="Search name" onSearch={(q) => list.setQuery({ q }, { replace: true })} />
      <Table
        rowKey="id"
        dataSource={state.page.items}
        columns={buildFlaggedAreaColumns({ list, onSetActive })}
        loading={isRefreshing}
        scroll={{ x: 'max-content' }}
        locale={list.isFiltered ? { emptyText: <NoMatchingAreas /> } : undefined}
        {...list.tableProps<FlaggedArea>(state.page.total)}
      />
    </div>
  );
}

function NoMatchingAreas() {
  return <EmptyState icon={SearchX} title="No matching flagged areas" description="No flagged areas match this search or these filters." />;
}
